import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Search, X, HelpCircle } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import React from "react";

export type SearchFiltersState = {
  name: string;
  city: string;
  community: string;
  minCapacity: string;
  maxOfficialPrice: string;
  maxCommunityPrice: string;
  type: string;
  sortBy: string;
};

type Props = {
  filters: SearchFiltersState;
  onChange: (filters: SearchFiltersState) => void;
  onSearch?: () => void;
};

/** Block non-numeric keys in number inputs */
const blockInvalidNumericKeys = (e: React.KeyboardEvent<HTMLInputElement>) => {
  if (["e", "E", "+", "-", ".", ","].includes(e.key)) {
    e.preventDefault();
  }
};

export function SearchFilters({ filters, onChange, onSearch }: Props) {
  const { data: communities } = useQuery({
    queryKey: ["filter-communities"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("venues")
        .select("autonomous_community")
        .eq("status", "active");
      if (error) throw error;
      const unique = [...new Set(data.map((v) => v.autonomous_community).filter(Boolean))] as string[];
      return unique.sort();
    },
    staleTime: 1000 * 60 * 10,
  });

  const update = (key: keyof SearchFiltersState, value: string) => {
    onChange({ ...filters, [key]: value });
  };

  /** Sanitize numeric inputs: only allow non-negative integers */
  const updateNumeric = (key: keyof SearchFiltersState, value: string) => {
    if (value === "") {
      onChange({ ...filters, [key]: "" });
      return;
    }
    const parsed = parseInt(value);
    if (Number.isInteger(parsed) && parsed >= 0) {
      onChange({ ...filters, [key]: String(parsed) });
    }
    // Invalid values are silently ignored (field keeps previous value)
  };

  const clearAll = () => {
    onChange({ name: "", city: "", community: "", minCapacity: "", maxOfficialPrice: "", maxCommunityPrice: "", type: "", sortBy: "name" });
  };

  const hasFilters = filters.name || filters.city || filters.community || filters.minCapacity || filters.maxOfficialPrice || filters.maxCommunityPrice || filters.type;

  return (
    <div className="space-y-4">
      {/* Row 1: Name */}
      <div>
        <Label className="mb-1.5 block text-xs font-medium text-muted-foreground">Nombre de la sala</Label>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Buscar por nombre..."
            value={filters.name}
            onChange={(e) => update("name", e.target.value)}
            className="pl-9"
          />
        </div>
      </div>

      {/* Row 2: City, Community, Type */}
      <div className="grid gap-3 sm:grid-cols-3">
        <div>
          <Label className="mb-1.5 block text-xs font-medium text-muted-foreground">Ciudad</Label>
          <Input
            placeholder="Ej: Madrid"
            value={filters.city}
            onChange={(e) => update("city", e.target.value)}
          />
        </div>
        <div>
          <Label className="mb-1.5 block text-xs font-medium text-muted-foreground">Comunidad Autónoma</Label>
          <Select value={filters.community || "all"} onValueChange={(v) => update("community", v)}>
            <SelectTrigger>
              <SelectValue placeholder="Todas" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas</SelectItem>
              {communities?.map((c) => (
                <SelectItem key={c} value={c}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="mb-1.5 block text-xs font-medium text-muted-foreground">Tipo de sala</Label>
          <Select value={filters.type || "all"} onValueChange={(v) => update("type", v)}>
            <SelectTrigger>
              <SelectValue placeholder="Todos" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los tipos</SelectItem>
              <SelectItem value="sala">Sala</SelectItem>
              <SelectItem value="teatro">Teatro</SelectItem>
              <SelectItem value="auditorio">Auditorio</SelectItem>
              <SelectItem value="exterior">Exterior</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Row 3: Capacity, Official Price, Community Price */}
      <div className="grid gap-3 sm:grid-cols-3">
        <div>
          <Label className="mb-1.5 block text-xs font-medium text-muted-foreground">Aforo mínimo</Label>
          <Input
            type="number"
            placeholder="Ej: 100"
            min={0}
            value={filters.minCapacity}
            onChange={(e) => updateNumeric("minCapacity", e.target.value)}
            onKeyDown={blockInvalidNumericKeys}
          />
        </div>
        <div>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Label className="mb-1.5 flex items-center gap-1 text-xs font-medium text-muted-foreground cursor-help">
                  Precio máx. Oficial (€) <HelpCircle className="h-3 w-3" />
                </Label>
              </TooltipTrigger>
              <TooltipContent className="max-w-[220px]">
                <p>Precio establecido oficialmente por la sala.</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <Input
            type="number"
            placeholder="Ej: 2000"
            min={0}
            value={filters.maxOfficialPrice}
            onChange={(e) => updateNumeric("maxOfficialPrice", e.target.value)}
            onKeyDown={blockInvalidNumericKeys}
          />
        </div>
        <div>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Label className="mb-1.5 flex items-center gap-1 text-xs font-medium text-muted-foreground cursor-help">
                  Precio máx. Comunidad (€) <HelpCircle className="h-3 w-3" />
                </Label>
              </TooltipTrigger>
              <TooltipContent className="max-w-[220px]">
                <p>Media de los precios reportados por los usuarios en sus reviews.</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <Input
            type="number"
            placeholder="Ej: 1500"
            min={0}
            value={filters.maxCommunityPrice}
            onChange={(e) => updateNumeric("maxCommunityPrice", e.target.value)}
            onKeyDown={blockInvalidNumericKeys}
          />
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-center gap-3 pt-1">
        <Button onClick={onSearch} className="gap-2 px-8">
          <Search className="h-4 w-4" />
          Buscar Sala
        </Button>
        {hasFilters && (
          <Button variant="ghost" size="sm" onClick={clearAll} className="gap-1 text-muted-foreground">
            <X className="h-3 w-3" />
            Limpiar
          </Button>
        )}
      </div>
    </div>
  );
}
